import React, { useEffect, useRef, useState } from 'react';
import { NumericInputWrapper, ButtonWrapper, Input, Icon24 } from './styles';
import { colors } from '@/globalStyles';
import MinusIcon from '@mui/icons-material/Remove';
import PlusIcon from '@mui/icons-material/Add';
import { handleDecimalNumberFieldChange } from '@/helpers';
import { useSelector, useDispatch } from 'react-redux';
import { setRemoveBlurFromAllInputs } from '@/actions';

const NumericInput = props => {
  const {
    onInputChange,
    value: _value,
    step,
    steps,
    min,
    max,
    iconPlus,
    iconMinus,
    iconColor,
    hoverColor,
    selectedColor,
    dataValue = '',
    disabled,
    gridTemplateColumns,
    gridTemplateRows,
    backgroundColor,
    borderRadius,
    ...restProps
  } = props;
  const blurAllInputs = useSelector(state => state.core.blurAllInputs);
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const inputValueRef = useRef(null);
  const [inputValue, setInputValue] = useState(_value);
  const value = +inputValue;
  const isMinusDisabled = min ? value <= min : false;
  const isPlusDisabled = max ? value >= max : false;

  inputValueRef.current = value;

  useEffect(() => {
    return () => {
      validateAndSetValue(inputValueRef.current || 0);
    };
  }, []);

  // in case if selected section was changed, we need to update the input value
  useEffect(() => {
    if (_value !== inputValue) {
      setInputValue(_value);
    }
  }, [_value]);

  useEffect(() => {
    if (blurAllInputs) {
      inputRef.current?.blur();
      dispatch(setRemoveBlurFromAllInputs(false));
    }
  }, [blurAllInputs, dispatch]);

  const validateAndSetValue = payload => {
    if (!!max && payload > max) {
      handleSetValue(max);
    } else if (!!min && payload < min) {
      handleSetValue(min);
    }
  };

  const handleFocus = () => {
    inputRef.current?.select();
  };

  const handleBlur = () => {
    validateAndSetValue(value);
  };

  const handleMinusClick = () => {
    if (disabled || isMinusDisabled) {
      return;
    }
    if (steps?.length) {
      // snap to the closest allowed value strictly below the current one
      const prev = steps.filter(s => s < value).sort((a, b) => b - a)[0];
      handleSetValue(prev !== undefined ? prev : steps[0]);
      return;
    }
    const payload = value - step;
    const newValue = Math.round(payload * 100) / 100;
    if (!!min) {
      if (value <= min) {
        handleSetValue(min);
      } else {
        handleSetValue(newValue);
      }
    } else {
      handleSetValue(newValue);
    }
  };

  const handleChange = e => {
    const result = handleDecimalNumberFieldChange(e.target.value);
    setInputValue(result);
    if (result) {
      if (!!max && result > max) {
        handleSetValue(max);
      } else if (!!min && result < min) {
        return;
      } else {
        handleSetValue(result);
      }
    } else {
      setInputValue('');
    }
  };

  const handlePlusClick = () => {
    if (disabled || isPlusDisabled) {
      return;
    }
    if (steps?.length) {
      // snap to the closest allowed value strictly above the current one
      const next = steps.filter(s => s > value).sort((a, b) => a - b)[0];
      handleSetValue(next !== undefined ? next : steps[steps.length - 1]);
      return;
    }
    const payload = value + step;
    const newValue = Math.round(payload * 100) / 100;
    if (!!max) {
      if (value < min) {
        handleSetValue(min);
      } else if (value < max) {
        handleSetValue(newValue);
      }
    } else {
      handleSetValue(newValue);
    }
  };

  const handleSetValue = payload => {
    setInputValue(payload);
    onInputChange(payload);
  };

  return (
    <NumericInputWrapper
      $gridTemplateColumns={gridTemplateColumns}
      $gridTemplateRows={gridTemplateRows}
      $backgroundColor={backgroundColor}
      $borderRadius={borderRadius}
      $disabled={disabled}
      {...restProps}
    >
      <ButtonWrapper
        data-value={`${dataValue}-minus`}
        onClick={handleMinusClick}
      >
        {!!iconMinus ? (
          <Icon24
            $disabled={isMinusDisabled}
            $hoverBoxShadow={true}
            src={iconMinus}
          />
        ) : (
          <MinusIcon
            style={{ color: iconColor ? iconColor : colors.primary }}
          />
        )}
      </ButtonWrapper>
      <Input
        ref={inputRef}
        $hoverColor={hoverColor}
        $selectedColor={selectedColor}
        $backgroundColor={backgroundColor}
        data-value={`${dataValue}-value`}
        disabled={disabled}
        value={inputValue}
        type="number"
        inputMode="decimal"
        pattern="d\*"
        step={step || 1}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...restProps}
      />
      <ButtonWrapper data-value={`${dataValue}-plus`} onClick={handlePlusClick}>
        {!!iconPlus ? (
          <Icon24
            $disabled={isPlusDisabled}
            $hoverBoxShadow={true}
            src={iconPlus}
          />
        ) : (
          <PlusIcon style={{ color: iconColor ? iconColor : colors.primary }} />
        )}
      </ButtonWrapper>
    </NumericInputWrapper>
  );
};

export default NumericInput;
